// Port of Offline/OfflineDownloadManager.swift (TrailBivvyKit): downloads offline packages — route data first (usable
// immediately), then map tiles.
//
// - Resumable: tile-level idempotency (tiles already in 'tb-tiles-v1' are skipped, whichever region stored them),
//   progress persisted every 50 tiles, plan persisted so `resume` works after a reload.
// - `pause` aborts in-flight requests and marks the region `paused`; `resume` replans the remaining tiles; `cancel`
//   removes the region, its plan, the tiles no other region's plan references and the data files no other region pins.
// - Bounded concurrency (≤ 6 requests in flight) plus ≥ 60 ms between request starts across all downloads, keeping
//   bulk downloads within the geo.admin.ch fair-use limits (1,200 WMTS requests/minute).
// - Failed tiles are counted: the region ends `failed` if more than 2 % fail, otherwise `completed`.
// - Losing connectivity mid-download pauses the region (resumable) and rejects with `OfflineError` `offline`.
// - Storage is checked first: remaining tile bytes × 1.2 must fit in `navigator.storage.estimate()`'s quota − usage;
//   `navigator.storage.persist()` is requested before the first download so the browser does not evict the tiles.
//
// Web deviations:
// - Route data = pinning the trail pack JSON and the world rules layer in 'tb-data-pinned-v1' (`OfflineDataPackage`),
//   instead of saving a TrailDataset subset. When a file is neither cached nor reachable the region is paused like an
//   offline tile download (`offline`); an error answer fails the region with reason `dataUnavailable`.
// - `downloadedBytes` counts the real size of the tiles downloaded by this run plus the layers' average tile size for
//   tiles that were already stored (reading every stored entry's size would cost one Cache Storage lookup per tile).
// - Regulation refresh re-downloads the pinned files (`refreshRegulations`); the caller computes the as-of date of the
//   refreshed content (it owns the pack decoder).
// - No task cancellation in JavaScript: `pause(regionID)` is the only way to stop a running download.

import type { EpochMs, OfflineRegion, OfflineRegionStatus } from '../core/models';
import type { ConnectivityProviding } from '../net/connectivity';
import { CacheNames, normalizedURL, PinnedDataCache, TileCache } from './OfflineCaches';
import { planFromStored, type OfflinePackagePlan } from './OfflinePackagePlanner';
import type { OfflineStore } from './OfflineStore';
import { isPlausibleTileData, layerContentType, layerTileURL, type TileLayerDescriptor } from './TileLayerDescriptor';
import type { TileCoordinate } from './TileMath';

/** Events published by `OfflineDownloadManager`. */
export type OfflineDownloadEvent =
  /** Tiles present / tiles planned, and bytes downloaded (including tiles that were already present). */
  | { kind: 'progress'; regionID: string; completedTiles: number; totalTiles: number; bytes: number }
  /** The persisted region changed (status, timestamps). A cancelled region is reported once with status
   *  `notDownloaded` and then no longer exists in the store. */
  | { kind: 'stateChanged'; region: OfflineRegion }
  /** All planned tiles were processed and at most 2 % failed. */
  | { kind: 'completed'; regionID: string }
  /** The download failed; `reason` is an `OfflineFailureReason` (a code, not UI text). */
  | { kind: 'failed'; regionID: string; reason: OfflineFailureReason };

/** Machine-readable reasons stored in `OfflineRegion.failureReason` and `failed` events. The UI localises them. */
export type OfflineFailureReason =
  /** Free storage was below the estimated need before starting. */
  | 'insufficientStorage'
  /** More than 2 % of the planned tiles could not be downloaded. */
  | 'tooManyTileFailures'
  /** Writing a tile failed (typically: the quota filled up during the download). */
  | 'storageError'
  /** Web: a route data file answered with an error (pack missing on the server). */
  | 'dataUnavailable';

export type OfflineErrorKind =
  /** Not enough storage for the remaining tiles (`required` includes the 20 % safety margin). */
  | { kind: 'insufficientStorage'; required: number; available: number }
  /** The device is offline: route data was pinned when possible, tiles will download on `resume` once online. */
  | { kind: 'offline' }
  /** Unknown region (or its plan is missing). */
  | { kind: 'regionNotFound' }
  /** The region was cancelled while the call was waiting for it. */
  | { kind: 'cancelled' };

/** Error thrown by the Offline module (Swift `OfflineError`). */
export class OfflineError extends Error {
  constructor(readonly error: OfflineErrorKind) {
    super(`OfflineError.${error.kind}`);
    this.name = 'OfflineError';
  }
}

/** Route data of a package (web). */
export interface OfflineDataPackage {
  /** Same-origin JSON files the region needs offline (e.g. '/data/trails/<id>.json', '/data/world_regulations.json'). */
  urls: string[];
  /** As-of date of the package's regulatory content (latest coverage `asOf` / regulation retrieval date). */
  regulationsAsOf?: EpochMs;
}

/** The part of `navigator.storage` the manager uses. */
export interface StorageManagerLike {
  estimate?(): Promise<{ quota?: number; usage?: number }>;
  persist?(): Promise<boolean>;
  persisted?(): Promise<boolean>;
}

export interface OfflineDownloadManagerOptions {
  store: OfflineStore;
  /** Cache Storage (default `globalThis.caches`). */
  caches?: CacheStorage;
  /** fetch (default `globalThis.fetch`). */
  fetch?: typeof fetch;
  /** Default: `navigator.onLine`. */
  connectivity?: ConnectivityProviding;
  /** Default: `navigator.storage`. */
  storage?: StorageManagerLike;
  /** Maximum simultaneous tile requests (default 6). */
  maxConcurrentDownloads?: number;
  /** Minimum spacing between tile request starts across all downloads, ms (default 60; tests pass 0). */
  minimumRequestInterval?: number;
  /** Per-request timeout, ms (default 30 s). */
  requestTimeout?: number;
  /** Wall clock for timestamps (injected in tests). */
  now?: () => EpochMs;
  /** Monotonic clock for request pacing, ms (default `performance.now`). */
  monotonic?: () => number;
  /** Base URL for relative data URLs (default `location.origin`). */
  baseURL?: string;
}

type StopRequest = 'pause' | 'cancel';

type DownloadOutcome =
  | { kind: 'completed' }
  | { kind: 'failed'; reason: OfflineFailureReason }
  | { kind: 'paused' }
  | { kind: 'cancelled' }
  | { kind: 'offline' }
  | { kind: 'insufficientStorage'; required: number; available: number }
  | { kind: 'error'; error: unknown };

interface ActiveDownload {
  token: number;
  controller: AbortController;
  promise: Promise<DownloadOutcome>;
  stopRequest?: StopRequest;
}

interface TileJob {
  layer: TileLayerDescriptor;
  tile: TileCoordinate;
  url: string | undefined;
}

type TileOutcome =
  | { kind: 'stored'; bytes: number }
  | { kind: 'failed'; reason: 'invalidURL' | 'timeout' | 'offline' | 'transport' | 'http' | 'invalidResponse' }
  | { kind: 'storageFailed' }
  | { kind: 'cancelled' };

interface Inventory {
  remaining: TileJob[];
  presentCount: number;
  presentBytes: number;
  remainingEstimatedBytes: number;
}

function defaultConnectivity(): ConnectivityProviding {
  return {
    get isOnline() {
      return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
    },
  };
}

export class OfflineDownloadManager {
  /** Progress is written to the store every this many stored tiles. */
  static readonly progressCheckpointInterval = 50;
  /** A region fails when more than this fraction of its tiles fail. */
  static readonly maximumFailureFraction = 0.02;
  /** Safety factor applied to the estimated tile bytes for the storage check. */
  static readonly diskSpaceSafetyFactor = 1.2;

  readonly maxConcurrentDownloads: number;
  private readonly store: OfflineStore;
  private readonly tiles: TileCache;
  private readonly pins: PinnedDataCache;
  private readonly fetchFn: typeof fetch;
  private readonly connectivity: ConnectivityProviding;
  private readonly storage?: StorageManagerLike;
  private readonly now: () => EpochMs;
  private readonly monotonic: () => number;
  private readonly minimumRequestInterval: number;
  private readonly requestTimeout: number;
  private readonly baseURL: string | undefined;

  private readonly listeners = new Set<(event: OfflineDownloadEvent) => void>();
  private readonly active = new Map<string, ActiveDownload>();
  private paceQueue: Promise<void> = Promise.resolve();
  private lastRequestStart: number | undefined;
  private nextToken = 1;
  private persistenceRequested = false;

  constructor(options: OfflineDownloadManagerOptions) {
    this.store = options.store;
    const caches = options.caches ?? globalThis.caches;
    this.fetchFn = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.tiles = new TileCache(caches, CacheNames.tiles);
    this.pins = new PinnedDataCache(caches, this.fetchFn, CacheNames.pinnedData);
    this.connectivity = options.connectivity ?? defaultConnectivity();
    this.storage = options.storage ?? (typeof navigator === 'undefined' ? undefined : navigator.storage);
    this.maxConcurrentDownloads = Math.max(1, options.maxConcurrentDownloads ?? 6);
    this.minimumRequestInterval = Math.max(0, options.minimumRequestInterval ?? 60);
    this.requestTimeout = options.requestTimeout ?? 30_000;
    this.now = options.now ?? (() => Date.now());
    this.monotonic = options.monotonic ?? (() => performance.now());
    this.baseURL = options.baseURL ?? (typeof location === 'undefined' ? undefined : location.origin);
  }

  // MARK: - Public API

  /** Subscribes to every event emitted from now on. Returns the unsubscribe function. */
  subscribe(listener: (event: OfflineDownloadEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Saves the region and its plan, pins its route data, then downloads tiles. Resolves when the download completes,
   * fails (reported via status and events) or is paused; rejects with `OfflineError` (`offline`,
   * `insufficientStorage`, `cancelled`) or a storage error. If the region is already downloading, waits for that
   * download instead.
   */
  async start(plan: OfflinePackagePlan, data: OfflineDataPackage): Promise<void> {
    const regionID = plan.region.id;
    const running = this.active.get(regionID);
    if (running) return OfflineDownloadManager.rethrow(await running.promise);
    const outcome = await this.launch(regionID, (token, signal) => this.prepareAndDownload(plan, data, token, signal));
    OfflineDownloadManager.rethrow(outcome);
  }

  /** Aborts in-flight requests and marks the region `paused` (no-op when not downloading). */
  pause(regionID: string): void {
    const running = this.active.get(regionID);
    if (!running) return;
    running.stopRequest ??= 'pause';
    running.controller.abort();
  }

  /**
   * Re-pins missing route data and downloads the remaining tiles of a paused, failed or incomplete region. Rejects with
   * `regionNotFound` when the region or its plan is unknown.
   */
  async resume(regionID: string): Promise<void> {
    const running = this.active.get(regionID);
    if (running) return OfflineDownloadManager.rethrow(await running.promise);
    const record = await this.store.record(regionID);
    if (!record?.plan) throw new OfflineError({ kind: 'regionNotFound' });
    const again = this.active.get(regionID); // re-check after the suspension point
    if (again) return OfflineDownloadManager.rethrow(await again.promise);
    const plan = planFromStored(record.plan, record.region);
    const outcome = await this.launch(regionID, async (token, signal) => {
      const pinned = await this.pinData(regionID, record.dataURLs);
      if (pinned) return pinned;
      return this.downloadTiles(plan, token, signal);
    });
    OfflineDownloadManager.rethrow(outcome);
  }

  /**
   * Stops any download, deletes the region and its plan, the tiles no other region's plan references and the data
   * files no other region pins. (Swift `cancel(regionID:)`; the UI calls it "delete".)
   */
  async cancel(regionID: string): Promise<void> {
    const running = this.active.get(regionID);
    if (running) {
      running.stopRequest = 'cancel';
      running.controller.abort();
      await running.promise;
    }
    const record = await this.store.record(regionID);
    if (record) {
      try {
        await this.removeExclusiveContent(regionID, record.plan ? planFromStored(record.plan, record.region) : undefined, record.dataURLs);
      } catch (error) {
        console.error(`Cannot remove the content of region ${regionID}`, error);
      }
    }
    try {
      await this.store.delete(regionID);
    } catch (error) {
      console.error(`Cannot delete region ${regionID}`, error);
    }
    if (record) {
      this.emit({ kind: 'stateChanged', region: { ...record.region, status: 'notDownloaded', completedTiles: 0, downloadedBytes: 0 } });
    }
  }

  /**
   * Re-downloads the region's pinned data files (map tiles untouched) and sets `regulationsUpdatedAt` to the as-of date
   * of the refreshed content, as computed by `asOf` from the refreshed JSON documents (the previous date is kept when
   * it returns undefined, so unchanged rules never look newer than they are). Files whose answer is empty or invalid
   * keep their pinned copy (a source glitch must never drop known restrictions). Rejects with `regionNotFound`, or
   * `offline` when offline or when no file could be reached.
   */
  async refreshRegulations(regionID: string, asOf?: (documents: unknown[]) => EpochMs | undefined): Promise<void> {
    const record = await this.store.record(regionID);
    if (!record) throw new OfflineError({ kind: 'regionNotFound' });
    if (!this.connectivity.isOnline) throw new OfflineError({ kind: 'offline' });
    const documents: unknown[] = [];
    let reached = 0;
    for (const url of record.dataURLs) {
      const result = await this.pins.refresh(url);
      if (result.status === 'offline') continue;
      reached += 1;
      if (result.status === 'updated') documents.push(result.json);
    }
    if (record.dataURLs.length > 0 && reached === 0) throw new OfflineError({ kind: 'offline' });
    if (documents.length === 0) {
      console.error(`Empty regulatory update for region ${regionID} ignored`);
      return;
    }
    const date = asOf?.(documents);
    const updated = await this.store.updateRegion(regionID, (region) => {
      if (date !== undefined) region.regulationsUpdatedAt = date;
    });
    if (updated) this.emit({ kind: 'stateChanged', region: updated });
  }

  /** Whether tiles (or route data) of the region are currently being downloaded. */
  isDownloading(regionID: string): boolean {
    return this.active.has(regionID);
  }

  // MARK: - Download pipeline

  private async launch(
    regionID: string,
    body: (token: number, signal: AbortSignal) => Promise<DownloadOutcome>,
  ): Promise<DownloadOutcome> {
    const token = this.nextToken++;
    const controller = new AbortController();
    const entry: ActiveDownload = { token, controller, promise: Promise.resolve({ kind: 'cancelled' }) };
    entry.promise = (async () => {
      try {
        return await body(token, controller.signal);
      } catch (error) {
        return { kind: 'error', error } as DownloadOutcome;
      } finally {
        if (this.active.get(regionID)?.token === token) this.active.delete(regionID);
      }
    })();
    this.active.set(regionID, entry);
    return entry.promise;
  }

  private async prepareAndDownload(
    plan: OfflinePackagePlan,
    data: OfflineDataPackage,
    token: number,
    signal: AbortSignal,
  ): Promise<DownloadOutcome> {
    const regionID = plan.region.id;
    await this.requestPersistence();
    const timestamp = this.now();
    const region: OfflineRegion = { ...plan.region };
    const existing = await this.store.region(regionID);
    if (existing) region.createdAt = existing.createdAt;
    region.status = 'queued';
    delete region.failureReason;
    region.totalTiles = plan.totalTiles;
    region.dataUpdatedAt = timestamp;
    if (data.regulationsAsOf !== undefined) region.regulationsUpdatedAt = data.regulationsAsOf;
    else delete region.regulationsUpdatedAt;
    const dataURLs = [...new Set(data.urls.map((u) => normalizedURL(u, this.baseURL)))];
    try {
      // Region, plan and data list first: the route data is usable offline even if no tile ever arrives.
      await this.store.saveRecord({ region, dataURLs });
      await this.store.savePlan({ ...plan, region }, dataURLs);
    } catch (error) {
      console.error(`Cannot save region ${regionID}`, error);
      return { kind: 'error', error };
    }
    this.emit({ kind: 'stateChanged', region });
    const pinned = await this.pinData(regionID, dataURLs);
    if (pinned) return pinned;
    return this.downloadTiles({ ...plan, region }, token, signal);
  }

  /** Pins the route data; returns an outcome when the region cannot continue. */
  private async pinData(regionID: string, urls: readonly string[]): Promise<DownloadOutcome | undefined> {
    let offline = false;
    for (const url of urls) {
      let result;
      try {
        result = await this.pins.pin(url);
      } catch (error) {
        console.error(`Cannot pin ${url}`, error);
        return this.finishFailed(regionID, 'storageError', undefined);
      }
      if (result === 'unavailable') return this.finishFailed(regionID, 'dataUnavailable', undefined);
      if (result === 'offline') offline = true;
    }
    if (offline) return this.finishPaused(regionID, undefined, { kind: 'offline' });
    return undefined;
  }

  private async downloadTiles(plan: OfflinePackagePlan, token: number, signal: AbortSignal): Promise<DownloadOutcome> {
    const regionID = plan.region.id;
    const inventory = await this.inventory(plan);
    const total = plan.totalTiles;
    let completed = inventory.presentCount;
    let bytes = inventory.presentBytes;
    const progress = { completed, total, bytes };

    if (this.stopRequest(regionID, token) === 'cancel') return { kind: 'cancelled' };
    if (inventory.remaining.length === 0) return this.finishCompleted(regionID, progress);
    if (signal.aborted) return this.finishPaused(regionID, progress, { kind: 'paused' });

    const required = Math.ceil(inventory.remainingEstimatedBytes * OfflineDownloadManager.diskSpaceSafetyFactor);
    const available = await this.availableStorage();
    if (available !== undefined && available < required) {
      await this.finishFailed(regionID, 'insufficientStorage', progress);
      return { kind: 'insufficientStorage', required, available };
    }
    if (!this.connectivity.isOnline) return this.finishPaused(regionID, progress, { kind: 'offline' });

    const downloading = await this.persist(regionID, 'downloading', progress);
    if (!downloading) return { kind: 'cancelled' }; // region deleted meanwhile
    this.emit({ kind: 'stateChanged', region: downloading });
    this.emit({ kind: 'progress', regionID, completedTiles: completed, totalTiles: total, bytes });

    const jobs = inventory.remaining;
    let next = 0;
    let failures = 0;
    let sinceCheckpoint = 0;
    let wentOffline = false;
    let storageFailed = false;
    let stop = false;
    const failureLimit = OfflineDownloadManager.maximumFailureFraction * total;

    const worker = async () => {
      while (!stop && !signal.aborted && next < jobs.length) {
        await this.pace();
        if (stop || signal.aborted || next >= jobs.length) break;
        const job = jobs[next++];
        const outcome = await this.fetchTile(job, signal);
        switch (outcome.kind) {
          case 'stored':
            completed += 1;
            bytes += outcome.bytes;
            sinceCheckpoint += 1;
            this.emit({ kind: 'progress', regionID, completedTiles: completed, totalTiles: total, bytes });
            break;
          case 'failed':
            failures += 1;
            if (outcome.reason === 'offline') wentOffline = true;
            break;
          case 'storageFailed':
            storageFailed = true;
            break;
          case 'cancelled':
            break;
        }
        if (sinceCheckpoint >= OfflineDownloadManager.progressCheckpointInterval) {
          sinceCheckpoint = 0;
          await this.persist(regionID, undefined, { completed, total, bytes });
        }
        if (!this.connectivity.isOnline) wentOffline = true;
        if (signal.aborted || wentOffline || storageFailed || failures > failureLimit) stop = true;
      }
    };
    const workers = Math.min(this.maxConcurrentDownloads, jobs.length);
    await Promise.all(Array.from({ length: workers }, worker));

    const final = { completed, total, bytes };
    switch (this.stopRequest(regionID, token)) {
      case 'cancel':
        return { kind: 'cancelled' };
      case 'pause':
        return this.finishPaused(regionID, final, { kind: 'paused' });
      default:
        break;
    }
    if (signal.aborted) return this.finishPaused(regionID, final, { kind: 'paused' });
    if (storageFailed) return this.finishFailed(regionID, 'storageError', final);
    if (wentOffline) return this.finishPaused(regionID, final, { kind: 'offline' });
    if (failures > failureLimit) {
      console.error(`Region ${regionID}: ${failures} of ${total} tiles failed`);
      return this.finishFailed(regionID, 'tooManyTileFailures', final);
    }
    if (failures > 0) console.info(`Region ${regionID} completed with ${failures} missing tiles`);
    return this.finishCompleted(regionID, final);
  }

  // MARK: - Finalisation

  private async finishCompleted(regionID: string, p: Progress): Promise<DownloadOutcome> {
    const region = await this.persist(regionID, 'completed', p, this.now());
    if (!region) return { kind: 'cancelled' };
    this.emit({ kind: 'progress', regionID, completedTiles: p.completed, totalTiles: p.total, bytes: p.bytes });
    this.emit({ kind: 'stateChanged', region });
    this.emit({ kind: 'completed', regionID });
    return { kind: 'completed' };
  }

  private async finishPaused(regionID: string, p: Progress | undefined, outcome: DownloadOutcome): Promise<DownloadOutcome> {
    const region = await this.persist(regionID, 'paused', p);
    if (!region) return { kind: 'cancelled' };
    this.emit({ kind: 'stateChanged', region });
    return outcome;
  }

  private async finishFailed(regionID: string, reason: OfflineFailureReason, p: Progress | undefined): Promise<DownloadOutcome> {
    const region = await this.persist(regionID, 'failed', p, undefined, reason);
    if (!region) return { kind: 'cancelled' };
    this.emit({ kind: 'stateChanged', region });
    this.emit({ kind: 'failed', regionID, reason });
    return { kind: 'failed', reason };
  }

  /**
   * Updates progress fields (and optionally status / map timestamp) of the stored region without touching other
   * fields. Returns undefined when the region no longer exists or the write failed (logged).
   */
  private async persist(
    regionID: string,
    status: OfflineRegionStatus | undefined,
    p: Progress | undefined,
    mapUpdatedAt?: EpochMs,
    failureReason?: OfflineFailureReason,
  ): Promise<OfflineRegion | undefined> {
    try {
      return await this.store.updateRegion(regionID, (region) => {
        if (status) {
          region.status = status;
          if (status === 'failed' && failureReason) region.failureReason = failureReason;
          else delete region.failureReason;
        }
        if (p) {
          region.completedTiles = p.completed;
          region.totalTiles = p.total;
          region.downloadedBytes = p.bytes;
        }
        if (mapUpdatedAt !== undefined) region.mapUpdatedAt = mapUpdatedAt;
      });
    } catch (error) {
      console.error(`Cannot persist progress of ${regionID}`, error);
      return undefined;
    }
  }

  // MARK: - Helpers

  private stopRequest(regionID: string, token: number): StopRequest | undefined {
    const running = this.active.get(regionID);
    return running && running.token === token ? running.stopRequest : undefined;
  }

  private emit(event: OfflineDownloadEvent): void {
    for (const listener of [...this.listeners]) {
      try {
        listener(event);
      } catch (error) {
        console.error('Offline download listener failed', error);
      }
    }
  }

  /**
   * Waits until at least `minimumRequestInterval` has passed since the previous request start (across all downloads).
   * Callers are served one at a time and the delay is measured on the clock after waking, so a late timer can never
   * bunch two request starts together (Swift reserved fixed slots).
   */
  private async pace(): Promise<void> {
    if (this.minimumRequestInterval <= 0) return;
    const previous = this.paceQueue;
    let release: () => void = () => undefined;
    this.paceQueue = new Promise<void>((resolve) => (release = resolve));
    await previous;
    try {
      for (;;) {
        const wait = this.lastRequestStart === undefined ? 0 : this.lastRequestStart + this.minimumRequestInterval - this.monotonic();
        if (wait <= 0) break;
        await new Promise((resolve) => setTimeout(resolve, Math.ceil(wait)));
      }
      this.lastRequestStart = this.monotonic();
    } finally {
      release();
    }
  }

  /** Asks the browser once to keep the app's storage (tiles would otherwise be evictable under pressure). */
  private async requestPersistence(): Promise<void> {
    if (this.persistenceRequested) return;
    this.persistenceRequested = true;
    try {
      if (await this.storage?.persisted?.()) return;
      await this.storage?.persist?.();
    } catch (error) {
      console.info('Persistent storage not granted', error);
    }
  }

  /** Free bytes for the origin (quota − usage), or undefined when the browser does not tell. */
  private async availableStorage(): Promise<number | undefined> {
    try {
      const estimate = await this.storage?.estimate?.();
      if (!estimate || estimate.quota === undefined || estimate.usage === undefined) return undefined;
      return Math.max(0, estimate.quota - estimate.usage);
    } catch {
      return undefined;
    }
  }

  /** Splits a plan into stored tiles and tiles to download (lower zoom levels first, so an overview map is usable early). */
  private async inventory(plan: OfflinePackagePlan): Promise<Inventory> {
    const stored = await this.tiles.urls();
    const remaining: Array<{ order: number; job: TileJob }> = [];
    let presentCount = 0;
    let presentBytes = 0;
    let remainingBytes = 0;
    let order = 0;
    for (const layer of plan.layers) {
      for (const tile of plan.layerTiles[layer.id] ?? []) {
        const url = layerTileURL(layer, tile);
        if (url !== undefined && stored.has(normalizedURL(url))) {
          presentCount += 1;
          presentBytes += Math.max(0, layer.averageTileBytes);
        } else {
          remaining.push({ order, job: { layer, tile, url } });
          remainingBytes += Math.max(0, layer.averageTileBytes);
        }
        order += 1;
      }
    }
    remaining.sort((a, b) => a.job.tile.z - b.job.tile.z || a.order - b.order);
    return { remaining: remaining.map((r) => r.job), presentCount, presentBytes, remainingEstimatedBytes: remainingBytes };
  }

  /** Downloads and stores one tile. Never rejects: the outcome is aggregated by the caller. */
  private async fetchTile(job: TileJob, signal: AbortSignal): Promise<TileOutcome> {
    if (signal.aborted) return { kind: 'cancelled' };
    if (job.url === undefined) return { kind: 'failed', reason: 'invalidURL' };
    const controller = new AbortController();
    let timedOut = false;
    const onAbort = () => controller.abort();
    signal.addEventListener('abort', onAbort);
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.requestTimeout);
    try {
      let response: Response;
      try {
        response = await this.fetchFn(job.url, { mode: 'cors', credentials: 'omit', signal: controller.signal });
      } catch {
        if (signal.aborted) return { kind: 'cancelled' };
        if (timedOut) return { kind: 'failed', reason: 'timeout' };
        return { kind: 'failed', reason: this.connectivity.isOnline ? 'transport' : 'offline' };
      }
      if (!response.ok) return { kind: 'failed', reason: 'http' };
      let data: Uint8Array;
      try {
        data = new Uint8Array(await response.arrayBuffer());
      } catch {
        if (signal.aborted) return { kind: 'cancelled' };
        return { kind: 'failed', reason: timedOut ? 'timeout' : 'transport' };
      }
      if (!isPlausibleTileData(job.layer, data)) return { kind: 'failed', reason: 'invalidResponse' };
      try {
        await this.tiles.store(job.url, data, layerContentType(job.layer));
      } catch (error) {
        console.error('Cannot store tile', error);
        return { kind: 'storageFailed' };
      }
      return { kind: 'stored', bytes: data.length };
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    }
  }

  /** Deletes the tiles and pinned files of a region that no other region needs. */
  private async removeExclusiveContent(regionID: string, plan: OfflinePackagePlan | undefined, dataURLs: readonly string[]): Promise<void> {
    const others = await this.store.recordsExcluding(regionID);
    if (plan) {
      const keep = new Set<string>();
      for (const other of others) {
        if (!other.plan) continue;
        const otherPlan = planFromStored(other.plan, other.region);
        for (const layer of otherPlan.layers) {
          for (const tile of otherPlan.layerTiles[layer.id] ?? []) {
            const url = layerTileURL(layer, tile);
            if (url !== undefined) keep.add(normalizedURL(url));
          }
        }
      }
      const remove: string[] = [];
      for (const layer of plan.layers) {
        for (const tile of plan.layerTiles[layer.id] ?? []) {
          const url = layerTileURL(layer, tile);
          if (url !== undefined && !keep.has(normalizedURL(url))) remove.push(url);
        }
      }
      await this.tiles.remove(remove);
    }
    const keptData = new Set(others.flatMap((o) => o.dataURLs));
    await this.pins.unpin(dataURLs.filter((url) => !keptData.has(url)));
  }

  static rethrow(outcome: DownloadOutcome): void {
    switch (outcome.kind) {
      case 'completed':
      case 'failed':
      case 'paused':
        return;
      case 'cancelled':
        throw new OfflineError({ kind: 'cancelled' });
      case 'offline':
        throw new OfflineError({ kind: 'offline' });
      case 'insufficientStorage':
        throw new OfflineError({ kind: 'insufficientStorage', required: outcome.required, available: outcome.available });
      case 'error':
        throw outcome.error;
    }
  }
}

interface Progress {
  completed: number;
  total: number;
  bytes: number;
}
